import { useAemConfig } from '@as-react/common-components';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { makeGetHasConsentOnMarketingCookies } from '../redux/cookieConsent/selectors/CookieConsentSelector';
import { FREDHOPPER_TEST_TRACKER_KEY, identifyFredhopper, initFredhopper } from '../util/fredhopper';
import { useOAuth } from './useOAuth';
/**
 * Hook that manages the Fredhopper (Attraqt XO) tracking SDK lifecycle.
 *
 * Responsibilities:
 * - Calls xo.activity.init() after marketing cookie consent is granted
 * - Identifies the customer (addUserIdentity) on login or returning authenticated
 *   session — the SDK doesn't persist this across page loads, so it must be
 *   re-applied on every page load, unlike the session id it manages itself.
 *
 * Fredhopper is attached to the generic tracking emitter at module-load time via
 * `util/tracking`, so this hook doesn't need to subscribe explicitly.
 *
 * Mount this once at app root (Bootstrap).
 * Use emitter.emit() from components to send vendor-agnostic events.
 */
export const useFredhopperTracking = (debug = false) => {
    const { fredhopperTrackingConfig: config } = useAemConfig();
    const getHasMarketingConsent = useMemo(makeGetHasConsentOnMarketingCookies, []);
    const hasMarketingConsent = useSelector(getHasMarketingConsent);
    const { isLoggedIn, userInfo } = useOAuth();
    const customerUuid = userInfo === null || userInfo === void 0 ? void 0 : userInfo.external_uuid;
    useEffect(() => {
        var _a;
        if (!hasMarketingConsent) {
            return;
        }
        initFredhopper({
            isEnabled: (_a = config === null || config === void 0 ? void 0 : config.isEnabled) !== null && _a !== void 0 ? _a : true,
            trackerKey: (config === null || config === void 0 ? void 0 : config.trackerKey) || FREDHOPPER_TEST_TRACKER_KEY,
            region: config === null || config === void 0 ? void 0 : config.region,
        }, debug);
    }, [config === null || config === void 0 ? void 0 : config.isEnabled, config === null || config === void 0 ? void 0 : config.trackerKey, config === null || config === void 0 ? void 0 : config.region, hasMarketingConsent, debug]);
    useEffect(() => {
        if (!hasMarketingConsent || !isLoggedIn || !customerUuid) {
            return;
        }
        identifyFredhopper(customerUuid);
    }, [hasMarketingConsent, isLoggedIn, customerUuid]);
};
